/**
 * Actiekaart — telt af naar middernacht.
 *
 * WAT ER VERANDERDE (6 okt)
 * Was: vijf minuten per bezoeker, met de vervaltijd als kenmerk op de
 * winkelwagen. Nu: één deadline om middernacht, voor iedereen dezelfde.
 *
 * Daarmee vervalt een hoop machinerie, en dat is winst:
 *  · geen kenmerk op de winkelwagen meer, dus ook geen race met /cart/add.js
 *  · geen verouderd tabblad dat de klok opnieuw kan starten
 *  · geen eenmalige laatste kans die aan een gast te binden was — iets wat op
 *    een thema sowieso niet waterdicht kon (een privévenster kreeg een nieuwe
 *    mand en dus een nieuwe kans)
 *
 * WELKE MIDDERNACHT
 * Die van de winkel (Europe/Amsterdam). Liquid rekent de seconden tot
 * middernacht server-side uit en geeft ze mee; JavaScript hoeft niets over
 * tijdzones te weten. Een bezoeker in Londen ziet dus de deadline van de
 * winkel, niet die van zijn eigen klok — dat is ook de deadline waar de
 * aanbieding werkelijk aan hangt.
 *
 * De servertijd wordt bijgesteld met de Date-header van /cart.js, zodat een
 * verkeerd ingestelde telefoon toch de juiste resterende tijd ziet.
 *
 * Loopt de klok af, dan schuift de deadline een etmaal op in plaats van op
 * nul te blijven staan.
 */

const DAG = 86400 * 1000;
const UUR = 3600 * 1000;

/* Kleurtrap: dezelfde vijf kleuren als de mockup, nu op uren in plaats van
   minuten. Van boven naar beneden; de eerste die past wint. */
const TRAP = [
  { vanaf: 12 * UUR, stand: 'm5' },
  { vanaf: 6 * UUR, stand: 'm4' },
  { vanaf: 3 * UUR, stand: 'm3' },
  { vanaf: 1 * UUR, stand: 'm2' },
  { vanaf: 0, stand: 'm1' },
];

const STANDEN = ['m5', 'm4', 'm3', 'm2', 'm1'];

let afwijking = 0;

function nu() {
  return Date.now() + afwijking;
}

function wortel() {
  return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
}

/** Stelt de klok bij op de servertijd uit de Date-header. */
async function ijkKlok() {
  try {
    const r = await fetch(wortel() + 'cart.js', { headers: { Accept: 'application/json' } });
    const kop = r.headers.get('Date');
    if (!kop) return;
    const t = Date.parse(kop);
    if (!Number.isNaN(t)) afwijking = t - Date.now();
  } catch (fout) {
    /* zonder ijking loopt hij op de klok van het apparaat; dat is geen ramp */
  }
}

/** @param {string} naam @param {object} data */
function meld(naam, data) {
  try {
    document.dispatchEvent(new CustomEvent(naam, { detail: data, bubbles: true }));
    if (typeof window.dataLayer !== 'undefined') window.dataLayer.push({ event: naam, ...data });
  } catch (fout) {
    /* meten mag de kaart nooit stukmaken */
  }
}

class MivozaActiekaart {
  #wrap;
  #kaart;
  #teksten;
  #handle;
  #vervalt = 0;
  #tikker = null;
  #stand = '';
  #waarnemer = null;

  /** @param {HTMLElement} wrap */
  constructor(wrap) {
    this.#wrap = wrap;
    this.#kaart = wrap.querySelector('[data-mv-rs-kaart]');
    this.#teksten = JSON.parse(wrap.querySelector('[data-mv-rs-teksten]').textContent);
    this.#handle = wrap.dataset.handle || location.pathname;

    const serverSec = Number(wrap.dataset.nuServer || 0);
    const totMiddernacht = Number(wrap.dataset.totMiddernacht || 0);
    if (serverSec > 0 && totMiddernacht > 0) {
      afwijking = serverSec * 1000 - Date.now();
      this.#vervalt = serverSec * 1000 + totMiddernacht * 1000;
    }

    this.#waarnemer = new ResizeObserver(() => this.#meet());
    this.#waarnemer.observe(wrap);

    document.addEventListener('visibilitychange', this.#opZichtbaar);

    this.#meet();
    this.#teken();
    this.#tikAan();
    ijkKlok().then(() => this.#teken());
  }

  /**
   * De min-height uit de opmaak is de LAAGSTE stand, niet de hoogste. Daarom
   * alle standen één keer in het echte element zetten, de hoogste onthouden en
   * die vastleggen. Er wordt tussendoor niet geschilderd.
   */
  #meet() {
    // Onder 200 px kolombreedte wikkelt elke regel op een paar letters en komt
    // er een hoogte van rond de 1.900 px uit die daarna blijft staan.
    if (this.#wrap.clientWidth < 200) return;

    const bewaard = this.#stand;
    const klok = this.#kaart.querySelector('[data-mv-rs-klok]');
    const klokWas = klok.textContent;
    klok.textContent = '23:59:59';
    this.#kaart.style.minHeight = '0px';

    let hoogste = 0;
    for (const stand of STANDEN) {
      this.#vulStand(stand);
      hoogste = Math.max(hoogste, this.#kaart.getBoundingClientRect().height);
    }

    this.#kaart.style.minHeight = Math.ceil(hoogste) + 'px';
    klok.textContent = klokWas;
    this.#stand = '';
    if (bewaard) this.#vulStand(bewaard);
  }

  #opZichtbaar = async () => {
    if (document.visibilityState !== 'visible') return;
    // Een verborgen pagina draait geen rendering-stappen, dus daar levert de
    // ResizeObserver niets af. Dit is het eerste moment waarop meten zin heeft.
    this.#meet();
    await ijkKlok();
    this.#teken();
  };

  #teken() {
    let over = this.#vervalt - nu();

    // Middernacht gepasseerd: de volgende dag telt verder in plaats van op
    // nul te blijven hangen.
    while (over <= 0 && this.#vervalt > 0) {
      this.#vervalt += DAG;
      over = this.#vervalt - nu();
      meld('aktion_midnight_rollover', { handle: this.#handle });
    }

    const stand = (TRAP.find((t) => over >= t.vanaf) || TRAP[TRAP.length - 1]).stand;
    this.#zet(stand, over);
  }

  #tikAan() {
    if (this.#tikker) return;
    this.#tikker = setInterval(() => this.#teken(), 1000);
  }

  /** Leeg veld houdt de vorige stand vast. @param {string} stand @param {string} veld */
  #tekst(stand, veld) {
    let i = STANDEN.indexOf(stand);
    while (i >= 0) {
      const w = (this.#teksten[STANDEN[i]] || {})[veld];
      if (w && String(w).trim()) return this.#vulIn(String(w));
      i -= 1;
    }
    return '';
  }

  /**
   * Het geldformaat van deze winkel bevat HTML van een valuta-app. Wij
   * schrijven met textContent, dus zonder strippen leest de klant de tags.
   * @param {string} ruw
   */
  #kaal(ruw) {
    return String(ruw || '')
      .replace(/<[^>]*>/g, '')
      .trim();
  }

  /** @param {string} t */
  #vulIn(t) {
    const b = this.#teksten.bedragen || {};
    return t
      .replaceAll('{korting}', this.#kaal(b.korting))
      .replaceAll('{nu}', this.#kaal(b.nu))
      .replaceAll('{normaal}', this.#kaal(b.normaal));
  }

  /** @param {string} stand */
  #vulStand(stand) {
    if (stand === this.#stand) return;
    const k = this.#kaart;
    k.className = 'rs s' + stand.slice(1);
    k.querySelector('[data-mv-rs-eybtekst]').textContent = this.#tekst(stand, 'eyb');
    k.querySelector('[data-mv-rs-hl]').textContent = this.#tekst(stand, 'hl');
    k.querySelector('[data-mv-rs-note]').textContent = this.#tekst(stand, 'note');
    k.querySelector('[data-mv-rs-stip]').hidden = false;
    this.#stand = stand;
  }

  /** @param {string} stand @param {number} over */
  #zet(stand, over) {
    this.#vulStand(stand);

    const sec = Math.max(0, Math.floor(over / 1000));
    const u = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    this.#kaart.querySelector('[data-mv-rs-klok]').textContent =
      u + ':' + String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0');

    // De balk loopt over het hele etmaal: vol net na middernacht, leeg er net voor.
    const deel = Math.max(0, Math.min(1, over / DAG));
    this.#kaart.querySelector('[data-mv-rs-vul]').style.width = deel * 100 + '%';
  }
}

document.querySelectorAll('[data-mv-rs]').forEach(function (el) {
  new MivozaActiekaart(el);
});

/**
 * §8 — de urgency-regel meldt zich pas als hij werkelijk te zien is: bij de
 * helft in beeld, hoogstens één keer per paginaweergave. Een kale check bij
 * het laden telt renders en geen vertoningen.
 */
const urgency = document.querySelector('.mv-urg');
if (urgency && 'IntersectionObserver' in window) {
  const kijker = new IntersectionObserver(
    (regels) => {
      for (const r of regels) {
        if (!r.isIntersecting) continue;
        meld('urgency_bar_shown', { pad: location.pathname });
        kijker.disconnect();
      }
    },
    { threshold: 0.5 }
  );
  kijker.observe(urgency);
}
